import React, { useEffect, useState } from "react";
import { Box, makeStyles, Typography } from "@material-ui/core";
import AppColor from "utilities/AppColor";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import SearchWorkerInput from "../../../components/SearchInput/SearchWorkerInput";
import SearchFilter from "./components/SearchFilter";
import SearchList from "./components/SearchList";
import { Pagination } from "@material-ui/lab";
import {
  getLanguages, getSearchWorkerList,
  getSearchWorkerQueries, getSearchWorkerTotalElements, getSearchWorkerTotalPages,
  getSpecialties,
  getSupports,
  reduceSearchWorker,
  removeSearchWorkerQueries,
  setSearchWorkerQueries
} from "../../../store/slices/worker";
import { getCompanyDetail } from "../../../store/slices/company";
import { getAuthInfo } from "../../../store/slices/auth";
import { SYSTEM_ROLE } from "../../../config/AppConfig";
import moment from "moment";
import AppButton from "../../../components/CustomButtons/AppButton";
import { ArrowDropUp } from "@material-ui/icons";
import AppUtil from "../../../utilities/AppUtil";
import { useHistory } from "react-router-dom";
const styles = {
  container: {
    padding: "1rem",
  },
  title: {
    margin: '0 0 1rem 0',
    '& h5': {
      color: AppColor.mainColor,
    }
  },
  paper: {
    padding: '5rem',
    display: 'flex',
    justifyContent: 'center'
  },
  scrollTop: {
    backgroundColor: '#7d7c7c',
    color: 'white',
    boxShadow: 'none',
    borderRadius: 0
  }
};
const useStyles = makeStyles(styles);
const SearchWorker = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const user = useSelector(getAuthInfo);
  const queries = useSelector(getSearchWorkerQueries);
  const data = useSelector(getSearchWorkerList);
  const [address, setAddress] = useState('')
  const totalElements = useSelector(getSearchWorkerTotalElements);
  const totalPages = useSelector(getSearchWorkerTotalPages);
  const [specialties, setSpecialties] = useState([]);
  const [supports, setSupports] = useState([]);
  const [languages, setLanguages] = useState([]);
  const specialtyList = useSelector(getSpecialties);
  const supportList = useSelector(getSupports);
  const languageList = useSelector(getLanguages);
  const companyDetail = useSelector(getCompanyDetail);

  const _scrollTop = () => {
    AppUtil.scrollToTopById("main-panel");
  }

  const _onPageChange = (event, page) => {
    const newQueries = {
      ...queries,
      page
    };
    _loadData(newQueries);
    _scrollTop();
  }

  const _search = (data) => {
    setAddress(data.search);
    const newQueries = {
      ...queries,
      lat: data.latLng?.lat || '',
      lng: data.latLng?.lng || '',
      search: data.search,
      page: 1
    };
    _loadData(newQueries);
  }

  const _onFilter = (data) => {
    _loadData({
      ...queries,
      ...data,
      page: 1
    });
  }

  const _loadData = async (data) => {
    const storedQueries = {
      ...data,
      genders: data.gender
        ? data?.gender?.map(e => typeof e === 'string' ? e : e.value)
        : data.genders,
      languageIds: data.languages
        ? data?.languages
        : data.languageIds,
      accreditationIds: data.accreditations
        ? data.accreditations?.map(e => typeof e !== 'object' ? e : e.value)
        : data.accreditationIds,
      specialtyIds: data.specialties
        ? data.specialties?.map(e => typeof e !== 'object' ? e : e.value)
        : data.specialtyIds,
      supportIds: data.supports
        ? data.supports?.map(e => typeof e !== 'object' ? e : e.value)
        : data.supportIds,
      ...!!data.datetime && {
        dateTime: moment(new Date(data.datetime)).format('YYYY/MM/DD HH:mm')
      }
    }
    const newQueries = {
      ...data,
      genders: data.gender
        ? data?.gender?.length === 3 ? [] : data?.gender?.map(e => typeof e === 'string' ? e : e.value) || []
        : data.genders,
      languageIds: data.languages
        ? languages?.length === data?.languages?.length ? [] : data?.languages || []
        : data.languageIds,
      accreditationIds: data.accreditations
        ? data.accreditations?.map(e => typeof e !== 'object' ? e : e.value) || []
        : data.accreditationIds,
      specialtyIds: data.specialties
        ? data.specialties?.length === specialties?.length ? [] : data.specialties?.map(e => typeof e !== 'object' ? e : e.value) || []
        : data.specialtyIds,
      supportIds: data.supports
        ? data.supports?.length === supports?.length ? data.supports?.map(e => typeof e !== 'object' ? e : e.value) : data.supports?.map(e => typeof e !== 'object' ? e : e.value) || []
        : data.supportIds,
      ...!!data.datetime && {
        dateTime: moment(new Date(data.datetime)).format('YYYY/MM/DD HH:mm')
      }
    }
    if (!data.datetime) {
      delete newQueries.dateTime;
      delete storedQueries.dateTime;
    }
    delete storedQueries.gender;
    delete storedQueries.supports;
    delete storedQueries.specialties;
    delete storedQueries.languages;
    delete storedQueries.accreditations;

    delete newQueries.gender;
    delete newQueries.supports;
    delete newQueries.specialties;
    delete newQueries.languages;
    delete newQueries.accreditations;
    await dispatch(setSearchWorkerQueries(storedQueries));
    await dispatch(reduceSearchWorker(newQueries));
  }

  useEffect(() => {
    if (history.location.state) {
      _loadData(queries);
    } else {
      _loadData({
        page: 1,
        size: 20,
        accreditationIds: [],
        genders: [],
        languageIds: [],
        lat: companyDetail?.company?.lat || '',
        lng: companyDetail?.company?.lng || '',
        specialtyIds: [],
        supportIds: [],
        search: ''
    });

    }
  }, [])

  useEffect(() => {
    if (companyDetail && queries && !queries.lat && !queries.lng) {
      _loadData({...queries,  lat: companyDetail?.company?.lat, lng: companyDetail?.company?.lng })
    }
  }, [companyDetail]);

  useEffect(() => {
    setSpecialties(specialtyList);
  }, [specialtyList])

  useEffect(() => {
    setSupports(supportList);
  }, [supportList])

  useEffect(() => {
    setLanguages(languageList);
  }, [languageList])

  return (
    <Box>
      <Box className={classes.title}>
        <Typography variant="h5" align="center">
          {user.role === SYSTEM_ROLE.HOSPITAL_ADMIN || user.role === SYSTEM_ROLE.HOSPITAL_OTHER ? t('title.search_for_nurses') : t('title.search_for_providers')}
        </Typography>
      </Box>
      <Box>
        <SearchWorkerInput value={queries.search} queries={queries} onSearch={_search} />
      </Box>
      <SearchFilter onSubmit={_onFilter} queries={queries} removeSearchWorkerQueries={() => dispatch(removeSearchWorkerQueries())} />
      {totalElements > 0 && (
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Pagination
            count={totalPages}
            variant="outlined"
            shape="rounded"
            page={queries.page}
            onChange={_onPageChange}
          />
        </Box>
      )}
      <SearchList
        data={data}
        totalElements={totalElements}
        address={address}
      />
      {totalElements > 0 && (
        <Box my={3} display="flex" justifyContent="flex-end">
          <Pagination
            count={totalPages}
            variant="outlined"
            shape="rounded"
            page={queries.page}
            onChange={_onPageChange}
          />
        </Box>
      )}
      {data.length > 2 && (
        <Box display="flex" justifyContent="center" alignItems="center">
          <AppButton
            label={t('label.scroll_to_top')}
            startIcon={<ArrowDropUp style={{ fontSize: 32 }} />}
            className={styles.scrollTop}
            buttonColor={'#7d7c7c'}
            disableElevation
            style={{ borderRadius: 0 }}
            onClick={_scrollTop}
          />
        </Box>
      )}
    </Box>
  );
};

export default SearchWorker;